import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppBase } from "@/hooks/useAppBase";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Mail, Plus, X, Trash2, Send, Users, Loader2,
  FileText, Megaphone, Edit3, ArrowLeft, CheckCircle2,
  TrendingUp, MousePointerClick, UserX, AlertCircle,
  Clock, Save, Pencil,
} from "lucide-react";

/**
 * Email Marketing hub — Templates list + Campaigns (con bozze).
 * Il log invii non è più un tab principale: si accede dalla scheda campagna.
 */
export default function EmailMarketing() {
  const [tab, setTab] = useState("templates");
  const [activeCampaignId, setActiveCampaignId] = useState(null);

  if (activeCampaignId) {
    return (
      <CampaignDetail
        campaignId={activeCampaignId}
        onBack={() => setActiveCampaignId(null)}
      />
    );
  }

  return (
    <div data-testid="email-page" className="space-y-8">
      <header>
        <h1 className="font-display text-2xl sm:text-4xl font-black tracking-tight inline-flex items-center gap-3">
          <Mail className="h-8 w-8 text-[color:var(--sp-brand)]" /> Email Marketing
        </h1>
        <p className="text-[color:var(--sp-text-secondary)] mt-2 max-w-2xl">
          Newsletter, promo di compleanno, riattivazione clienti. Crea il template con l&apos;editor
          visuale, spediscilo a un segmento in un click.
        </p>
      </header>

      <div className="flex items-center gap-2 border-b border-[color:var(--sp-border)]">
        <TabBtn active={tab === "templates"} onClick={() => setTab("templates")} tid="tab-templates" icon={FileText}>Template</TabBtn>
        <TabBtn active={tab === "campaigns"} onClick={() => setTab("campaigns")} tid="tab-campaigns" icon={Megaphone}>Campagne</TabBtn>
      </div>

      {tab === "templates" && <TemplatesTab />}
      {tab === "campaigns" && <CampaignsTab onOpen={setActiveCampaignId} />}
    </div>
  );
}

function TabBtn({ active, onClick, children, tid, icon: Icon }) {
  return (
    <button
      data-testid={tid}
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-bold inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
        active ? "border-[color:var(--sp-brand)] text-[color:var(--sp-brand)]" : "border-transparent text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-text)]"
      }`}
    >
      <Icon className="h-4 w-4" />
      {children}
    </button>
  );
}

/* ------------------------------- Templates ------------------------------- */
function TemplatesTab() {
  const nav = useNavigate();
  const base = useAppBase();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(null); // {id, name}
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setItems((await api.get("/email/templates")).data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await api.delete(`/email/templates/${confirmDel.id}`);
      toast.success("Template eliminato");
      setConfirmDel(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <section className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-[color:var(--sp-text-muted)]">{items.length} template salvati</div>
        <button data-testid="new-template-btn" onClick={() => nav(`${base}/email/templates/new`)} className="sp-btn-primary inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> Nuovo template
        </button>
      </div>

      {loading ? (
        <div className="sp-card p-12 text-center text-[color:var(--sp-text-muted)]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="sp-card p-12 text-center text-[color:var(--sp-text-muted)]">
          <Mail className="h-8 w-8 mx-auto mb-3 text-[color:var(--sp-border-strong)]" />
          Nessun template. Crea il primo per iniziare a inviare campagne.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <div
              key={t.id}
              data-testid={`tpl-${t.id}`}
              onClick={() => nav(`${base}/email/templates/${t.id}`)}
              className="sp-card p-5 hover:border-[color:var(--sp-brand)]/40 hover:shadow-md transition-all cursor-pointer group"
            >
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-lg font-black mt-1 truncate">{t.name}</div>
                  <div className="text-xs text-[color:var(--sp-text-muted)] mt-1 truncate">Oggetto: {t.subject}</div>
                </div>
                <button
                  data-testid={`tpl-delete-${t.id}`}
                  onClick={(e) => { e.stopPropagation(); setConfirmDel({ id: t.id, name: t.name }); }}
                  className="text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)]"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-xs text-[color:var(--sp-brand)] font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                <Edit3 className="h-3 w-3" /> Modifica
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDel}
        title="Eliminare il template?"
        description={confirmDel ? `"${confirmDel.name}" verrà rimosso definitivamente. Le campagne già inviate con questo template restano invariate.` : ""}
        confirmLabel="Elimina template"
        tone="danger"
        onConfirm={doDelete}
        onClose={() => !busy && setConfirmDel(null)}
        busy={busy}
      />
    </section>
  );
}

/* ------------------------------- Campaigns ------------------------------- */
function CampaignsTab({ onOpen }) {
  const [campaigns, setCampaigns] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null); // { campaign }
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | draft | scheduled | sent
  const [confirmDel, setConfirmDel] = useState(null); // { id, name, status }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setCampaigns((await api.get("/email/campaigns")).data); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await api.delete(`/email/campaigns/${confirmDel.id}`);
      toast.success("Campagna eliminata");
      setConfirmDel(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return campaigns;
    if (filter === "draft") return campaigns.filter((c) => c.status === "draft");
    if (filter === "scheduled") return campaigns.filter((c) => c.status === "scheduled");
    return campaigns.filter((c) => c.status !== "draft" && c.status !== "scheduled");
  }, [campaigns, filter]);

  const draftCount = campaigns.filter((c) => c.status === "draft").length;
  const scheduledCount = campaigns.filter((c) => c.status === "scheduled").length;

  return (
    <section className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1" data-testid="campaigns-filter">
          {[
            ["all", `Tutte (${campaigns.length})`],
            ["draft", `Bozze (${draftCount})`],
            ["scheduled", `Programmate (${scheduledCount})`],
            ["sent", `Inviate (${campaigns.length - draftCount - scheduledCount})`],
          ].map(([k, l]) => (
            <button key={k} data-testid={`campaigns-filter-${k}`} onClick={() => setFilter(k)}
                    className={`px-3 py-1 rounded-full text-xs font-bold ${filter === k ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
              {l}
            </button>
          ))}
        </div>
        <button data-testid="new-campaign-btn" onClick={() => setCreating(true)} className="sp-btn-primary inline-flex items-center gap-2">
          <Plus className="h-4 w-4" /> Nuova campagna
        </button>
      </div>

      {loading ? (
        <div className="sp-card p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[color:var(--sp-text-muted)]" /></div>
      ) : filtered.length === 0 ? (
        <div className="sp-card p-12 text-center text-[color:var(--sp-text-muted)]">
          <Megaphone className="h-8 w-8 mx-auto mb-3 text-[color:var(--sp-border-strong)]" />
          {filter === "draft" ? "Nessuna bozza." : filter === "sent" ? "Nessuna campagna inviata ancora." : "Nessuna campagna. Crea un template e poi lancia il tuo primo invio."}
        </div>
      ) : (
        <div className="sp-card overflow-hidden divide-y divide-[color:var(--sp-border)]">
          {filtered.map((c) => (
            <CampaignRow key={c.id} c={c}
              onOpen={() => ["draft", "scheduled"].includes(c.status) ? setEditingDraft({ campaign: c }) : onOpen(c.id)}
              onReload={load}
              onRequestDelete={(camp) => setConfirmDel({ id: camp.id, name: camp.name, status: camp.status })}
            />
          ))}
        </div>
      )}

      {creating && (
        <CampaignModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
      {editingDraft && (
        <CampaignModal
          initial={editingDraft.campaign}
          onClose={() => setEditingDraft(null)}
          onSaved={() => { setEditingDraft(null); load(); }}
        />
      )}

      <ConfirmDialog
        open={!!confirmDel}
        title={
          confirmDel?.status === "scheduled" ? "Eliminare la campagna programmata?" :
          confirmDel?.status === "draft" ? "Eliminare la bozza?" :
          "Eliminare la campagna?"
        }
        description={confirmDel ? `"${confirmDel.name}" verrà eliminata definitivamente. Le statistiche verranno perse.` : ""}
        confirmLabel="Elimina campagna"
        tone="danger"
        onConfirm={doDelete}
        onClose={() => !busy && setConfirmDel(null)}
        busy={busy}
      />
    </section>
  );
}

function CampaignRow({ c, onOpen, onReload, onRequestDelete }) {
  const isDraft = c.status === "draft";
  const isScheduled = c.status === "scheduled";
  const isSending = c.status === "sending";
  const isDone = c.status === "done";
  const isPartial = c.status === "partial";
  const isFailed = c.status === "failed";
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (isDraft || isSending || isScheduled) return;
    api.get(`/email/campaigns/${c.id}`).then((r) => setStats(r.data.stats)).catch(() => {});
  }, [c.id, c.status, c.sent_count, isDraft, isSending, isScheduled]);

  const requestDelete = (e) => {
    e.stopPropagation();
    onRequestDelete(c);
  };

  return (
    <div
      className="p-5 flex items-center gap-4 flex-wrap hover:bg-[color:var(--sp-subtle)]/40 cursor-pointer transition-colors"
      onClick={onOpen}
      data-testid={`campaign-row-${c.id}`}
    >
      <StatusIcon status={c.status} />
      <div className="flex-1 min-w-[200px]">
        <div className="font-bold text-sm truncate flex items-center gap-2">
          {c.name}
          {isDraft && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 uppercase tracking-wider">Bozza</span>}
          {isScheduled && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 uppercase tracking-wider inline-flex items-center gap-1"><Clock className="h-2.5 w-2.5" />Programmata</span>}
          {c.mode === "scratch" && !isDone && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 uppercase tracking-wider">Newsletter</span>}
        </div>
        <div className="text-[11px] text-[color:var(--sp-text-muted)] mt-1 flex flex-wrap gap-3">
          <span>{c.mode === "scratch" ? "Contenuto: inline" : `Template: ${c.template_name || "—"}`}</span>
          <span>Audience: <strong className="text-[color:var(--sp-text)]">{c.audience_count}</strong></span>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {isScheduled && c.scheduled_at
              ? `Invio: ${new Date(c.scheduled_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}`
              : new Date(c.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
            }
          </span>
        </div>
      </div>

      {stats && c.sent_count > 0 && !isDraft && !isScheduled && (
        <div className="flex gap-2 text-[11px]" data-testid={`campaign-row-metrics-${c.id}`}>
          <Metric label="Aperture" value={`${stats.open_rate}%`} tone="emerald" />
          <Metric label="Click" value={`${stats.click_rate}%`} tone="brand" />
          <Metric label="Inviate" value={c.sent_count} tone="cyan" />
        </div>
      )}

      {isSending && (
        <div className="inline-flex items-center gap-2 text-xs text-cyan-700 font-bold">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Invio in corso…
        </div>
      )}

      {(isDraft || isScheduled) && (
        <div className="flex items-center gap-1">
          <button
            data-testid={`campaign-edit-draft-${c.id}`}
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            className="p-2 rounded-lg text-[color:var(--sp-text-muted)] hover:bg-[color:var(--sp-subtle)] hover:text-[color:var(--sp-brand)]"
            title={isDraft ? "Modifica bozza" : "Modifica programmata"}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            data-testid={`campaign-delete-draft-${c.id}`}
            onClick={requestDelete}
            className="p-2 rounded-lg text-[color:var(--sp-text-muted)] hover:bg-[color:var(--sp-danger)]/10 hover:text-[color:var(--sp-danger)]"
            title="Elimina"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}

      {(isDone || isPartial || isFailed) && !isDraft && !isScheduled && (
        <div className="flex items-center gap-2">
          <StatusPill status={c.status} />
          <button
            data-testid={`campaign-delete-${c.id}`}
            onClick={requestDelete}
            className="p-2 rounded-lg text-[color:var(--sp-text-muted)] hover:bg-[color:var(--sp-danger)]/10 hover:text-[color:var(--sp-danger)]"
            title="Elimina campagna"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/** Iconcina discreta a sinistra al posto della "spunta verde Done".
 *  - draft → matita
 *  - scheduled → clock indaco
 *  - sending → clock/loader cyan
 *  - done → Send tondo brand
 *  - partial → alert giallo
 *  - failed → alert rosso
 */
function StatusIcon({ status }) {
  const map = {
    draft:     { icon: Pencil,      color: "text-slate-500 bg-slate-100" },
    scheduled: { icon: Clock,       color: "text-indigo-600 bg-indigo-50" },
    sending:   { icon: Loader2,     color: "text-cyan-600 bg-cyan-50", spin: true },
    done:      { icon: Send,        color: "text-[color:var(--sp-brand)] bg-[color:var(--sp-brand)]/10" },
    partial:   { icon: AlertCircle, color: "text-amber-700 bg-amber-50" },
    failed:    { icon: AlertCircle, color: "text-rose-700 bg-rose-50" },
    empty:     { icon: UserX,       color: "text-slate-500 bg-slate-100" },
  };
  const { icon: Icon, color, spin } = map[status] || map.done;
  return (
    <span className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon className={`h-4 w-4 ${spin ? "animate-spin" : ""}`} />
    </span>
  );
}

function StatusPill({ status }) {
  const map = {
    done:    { label: "Inviata",  cls: "bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]" },
    partial: { label: "Parziale", cls: "bg-amber-100 text-amber-700" },
    failed:  { label: "Errore",   cls: "bg-rose-100 text-rose-700" },
    empty:   { label: "Vuota",    cls: "bg-slate-100 text-slate-700" },
  };
  const { label, cls } = map[status] || map.done;
  return <span className={`text-[10px] font-bold px-2 py-1 rounded-full flex-shrink-0 ${cls}`}>{label}</span>;
}

function Metric({ label, value, tone }) {
  const colors = {
    cyan: "text-cyan-700 bg-cyan-50",
    emerald: "text-emerald-700 bg-emerald-50",
    brand: "text-[color:var(--sp-brand)] bg-[color:var(--sp-brand)]/10",
    rose: "text-rose-700 bg-rose-50",
  };
  return (
    <div className={`px-2.5 py-1 rounded-lg text-center ${colors[tone] || "bg-slate-50 text-slate-700"}`}>
      <div className="text-[9px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-xs font-black">{value}</div>
    </div>
  );
}

/* ------------------------- Campaign Create / Edit ------------------------ */
function CampaignModal({ onClose, onSaved, initial }) {
  const isEdit = !!initial;
  const [templates, setTemplates] = useState([]);
  const [tags, setTags] = useState([]);
  const [name, setName] = useState(initial?.name || "");
  const [templateId, setTemplateId] = useState(initial?.template_id || "");
  const [selectedTags, setSelectedTags] = useState(initial?.segment?.tag_names || []);
  const [requireConsent, setRequireConsent] = useState(initial?.segment?.require_consent ?? true);
  const [scheduledAt, setScheduledAt] = useState(initial?.scheduled_at ? toLocalInput(initial.scheduled_at) : "");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/email/templates"), api.get("/tags")]).then(([t, g]) => {
      setTemplates(t.data); setTags(g.data);
    });
  }, []);

  const doPreview = async () => {
    try {
      const r = await api.post("/email/campaigns/preview", {
        name: name || "preview", template_id: templateId || null,
        tag_names: selectedTags.length ? selectedTags : null,
        require_consent: requireConsent,
      });
      setPreview(r.data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { doPreview(); /* eslint-disable-next-line */ }, [selectedTags, requireConsent]);

  const isValid = () => !!name.trim() && !!templateId;

  const submit = async (isDraft) => {
    if (!isValid()) { toast.error("Nome e template obbligatori"); return; }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        mode: "template",
        template_id: templateId,
        tag_names: selectedTags.length ? selectedTags : null,
        require_consent: requireConsent,
        is_draft: isDraft,
        scheduled_at: (!isDraft && scheduledAt) ? new Date(scheduledAt).toISOString() : null,
      };
      if (isEdit) {
        await api.patch(`/email/campaigns/${initial.id}`, payload);
        if (!isDraft) {
          if (payload.scheduled_at) {
            toast.success(`Programmata per ${new Date(scheduledAt).toLocaleString("it-IT")}`);
          } else {
            const r = await api.post(`/email/campaigns/${initial.id}/send`);
            if (r.data.audience_count === 0) toast.warning("Nessun destinatario — nessun invio.");
            else toast.success(`Campagna avviata — ${r.data.audience_count} destinatari`);
          }
        } else toast.success("Bozza salvata");
      } else {
        const r = await api.post("/email/campaigns", payload);
        if (isDraft) toast.success("Bozza salvata");
        else if (r.data.status === "scheduled") toast.success(`Programmata per ${new Date(scheduledAt).toLocaleString("it-IT")}`);
        else if (r.data.audience_count === 0) toast.warning("Nessun destinatario — nessun invio.");
        else toast.success(`Campagna avviata — ${r.data.audience_count} destinatari`);
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const toggleTag = (n) => {
    setSelectedTags(selectedTags.includes(n) ? selectedTags.filter((t) => t !== n) : [...selectedTags, n]);
  };

  const isScheduled = !!scheduledAt;
  const sendLabel = isScheduled ? "Programma invio" : "Avvia invio";

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="campaign-modal">
      <div className="sp-card w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-2xl font-black">
            {isEdit ? "Modifica campagna" : "Nuova campagna email"}
          </h3>
          <button onClick={onClose} data-testid="cmp-modal-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-bold">Nome interno</label>
            <input data-testid="cmp-name" className="sp-input mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Promo Estate 2026" />
          </div>

          <div>
            <label className="text-sm font-bold">Template</label>
            <select data-testid="cmp-template" className="sp-input mt-1" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              <option value="">— Scegli template —</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          <div>
            <label className="text-sm font-bold">Segmento — filtra per tag</label>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="cmp-tags">
              {tags.length === 0 && <span className="text-xs text-[color:var(--sp-text-muted)] italic">Nessun tag disponibile — tutti i clienti con email saranno inclusi.</span>}
              {tags.map((t) => {
                const active = selectedTags.includes(t.name);
                return (
                  <button
                    key={t.id} type="button"
                    onClick={() => toggleTag(t.name)}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-[color:var(--sp-brand)] text-white border-[color:var(--sp-brand)]" : "bg-white text-[color:var(--sp-text-secondary)] border-[color:var(--sp-border)]"}`}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">Vuoto = tutti i clienti con email. Un tag = clienti con quel tag.</div>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="cmp-consent" checked={requireConsent} onChange={(e) => setRequireConsent(e.target.checked)} className="h-4 w-4" />
            Solo clienti con consenso email (GDPR)
          </label>

          {/* Programma invio */}
          <div className="rounded-xl border border-[color:var(--sp-border)] p-3 bg-[color:var(--sp-subtle)]/30">
            <label className="flex items-center gap-2 text-sm font-bold">
              <Clock className="h-4 w-4 text-[color:var(--sp-brand)]" />
              Programma invio (opzionale)
            </label>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <input
                type="datetime-local"
                data-testid="cmp-scheduled-at"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                min={toLocalInput(new Date().toISOString())}
                className="sp-input !py-1.5 text-sm flex-1 min-w-[200px]"
              />
              {scheduledAt && (
                <button type="button" onClick={() => setScheduledAt("")} data-testid="cmp-scheduled-clear"
                        className="text-xs px-2 py-1 rounded-lg border border-[color:var(--sp-border)] hover:bg-white">
                  <X className="h-3 w-3 inline" /> Rimuovi
                </button>
              )}
            </div>
            <div className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">
              {scheduledAt ? `Verrà inviata il ${new Date(scheduledAt).toLocaleString("it-IT")}` : "Lascia vuoto per inviare subito"}
            </div>
          </div>

          {preview && (
            <div className="rounded-2xl bg-[color:var(--sp-brand)]/5 border border-[color:var(--sp-brand)]/20 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="h-4 w-4 text-[color:var(--sp-brand)]" />
                <span className="font-bold text-sm">Anteprima segmento</span>
              </div>
              <div className="text-4xl font-display font-black text-[color:var(--sp-brand)] tabular-nums" data-testid="cmp-audience-count">{preview.count}</div>
              <div className="text-xs text-[color:var(--sp-text-muted)]">destinatari saranno raggiunti</div>
            </div>
          )}

          <div className="flex gap-2 pt-2 flex-wrap">
            <button onClick={onClose} className="sp-btn-ghost">Annulla</button>
            <div className="flex-1" />
            <button
              data-testid="cmp-save-draft"
              disabled={busy || !isValid()}
              onClick={() => submit(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-[color:var(--sp-border)] text-sm font-bold hover:bg-[color:var(--sp-subtle)] disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Salva come bozza
            </button>
            <button
              data-testid="cmp-send"
              disabled={busy || !isValid()}
              onClick={() => submit(false)}
              className="sp-btn-primary inline-flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (isScheduled ? <Clock className="h-4 w-4" /> : <Send className="h-4 w-4" />)}
              {sendLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** ISO datetime → 'YYYY-MM-DDThh:mm' locale for <input type=datetime-local>. */
function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

/** Confirm dialog in-app — sostituisce il window.confirm nativo del browser
 *  con un'esperienza coerente col design Memento (rosso per azioni distruttive). */
function ConfirmDialog({ open, title, description, confirmLabel = "Conferma", tone = "danger", onConfirm, onClose, busy }) {
  if (!open) return null;
  const cls = tone === "danger"
    ? "bg-[color:var(--sp-danger)] text-white hover:opacity-90"
    : "bg-[color:var(--sp-brand)] text-white hover:opacity-90";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="confirm-dialog">
      <div className="sp-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <span className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${tone === "danger" ? "bg-rose-100 text-rose-600" : "bg-[color:var(--sp-brand)]/10 text-[color:var(--sp-brand)]"}`}>
            <AlertCircle className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg font-black">{title}</h3>
            {description && <p className="text-sm text-[color:var(--sp-text-secondary)] mt-1">{description}</p>}
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} data-testid="confirm-dialog-cancel"
                  className="px-4 py-2 rounded-xl border border-[color:var(--sp-border)] text-sm font-bold hover:bg-[color:var(--sp-subtle)]">
            Annulla
          </button>
          <button onClick={onConfirm} disabled={busy} data-testid="confirm-dialog-confirm"
                  className={`px-4 py-2 rounded-xl text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50 ${cls}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* --------------------------- Campaign Detail ----------------------------- */
function CampaignDetail({ campaignId, onBack }) {
  const [data, setData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logSearch, setLogSearch] = useState("");
  const [logFilter, setLogFilter] = useState("all"); // all | opened | clicked | error | unsub

  const load = async () => {
    try {
      const [c, l] = await Promise.all([
        api.get(`/email/campaigns/${campaignId}`),
        api.get(`/email/logs?limit=500&campaign_id=${campaignId}`),
      ]);
      setData(c.data); setLogs(l.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaignId]);

  const filteredLogs = useMemo(() => {
    const q = logSearch.trim().toLowerCase();
    return logs.filter((l) => {
      if (logFilter === "opened" && !(l.opens_count > 0)) return false;
      if (logFilter === "clicked" && !(l.clicks_count > 0)) return false;
      if (logFilter === "error" && l.status !== "error") return false;
      if (logFilter === "unsub" && !l.unsubscribed_at) return false;
      if (!q) return true;
      return `${l.to || ""} ${l.subject || ""}`.toLowerCase().includes(q);
    });
  }, [logs, logSearch, logFilter]);

  if (loading || !data) {
    return (
      <div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[color:var(--sp-text-muted)]" /></div>
    );
  }

  const stats = data.stats || {};
  const openedCount = logs.filter((l) => l.opens_count > 0).length;
  const clickedCount = logs.filter((l) => l.clicks_count > 0).length;
  const unsubCount = logs.filter((l) => !!l.unsubscribed_at).length;
  const errorCount = logs.filter((l) => l.status === "error").length;

  return (
    <div className="space-y-6" data-testid="campaign-detail-page">
      <header className="flex items-center gap-3 flex-wrap">
        <button data-testid="campaign-detail-back" onClick={onBack} className="sp-btn-ghost inline-flex items-center gap-1 text-sm">
          <ArrowLeft className="h-4 w-4" /> Torna alle campagne
        </button>
        <div className="flex-1 min-w-[200px]">
          <div className="label-eyebrow">Dettaglio campagna</div>
          <h1 className="font-display text-3xl font-black tracking-tight">{data.name}</h1>
          <div className="text-xs text-[color:var(--sp-text-muted)] mt-1 flex flex-wrap gap-3">
            <span>Template: <strong className="text-[color:var(--sp-text)]">{data.template_name || "—"}</strong></span>
            <span>Inviata: {new Date(data.created_at).toLocaleString("it-IT")}</span>
            <StatusPill status={data.status} />
          </div>
        </div>
      </header>

      {/* KPI grandi */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="campaign-kpi-grid">
        <KpiCard
          label="Consegna"
          value={`${stats.delivery_rate ?? 0}%`}
          sub={`${data.sent_count || 0} su ${data.audience_count || 0}`}
          icon={Send}
          tone="cyan"
          tid="kpi-delivery"
        />
        <KpiCard
          label="Aperture"
          value={`${stats.open_rate ?? 0}%`}
          sub={`${stats.opens_unique || 0} aperture uniche`}
          icon={TrendingUp}
          tone="emerald"
          tid="kpi-open"
        />
        <KpiCard
          label="Click"
          value={`${stats.click_rate ?? 0}%`}
          sub={`${stats.clicks_unique || 0} click unici`}
          icon={MousePointerClick}
          tone="brand"
          tid="kpi-click"
        />
        <KpiCard
          label="Disiscritti"
          value={stats.unsubscribes ?? 0}
          sub={`${data.error_count || 0} errori invio`}
          icon={UserX}
          tone="rose"
          tid="kpi-unsub"
        />
      </div>

      {/* Numeri assoluti secondari */}
      <div className="sp-card p-5">
        <div className="label-eyebrow mb-3">Metriche assolute</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <MiniStat label="Audience totale" value={data.audience_count || 0} />
          <MiniStat label="Consegnate" value={data.sent_count || 0} />
          <MiniStat label="Aperture totali" value={stats.opens_total || 0} />
          <MiniStat label="Click totali" value={stats.clicks_total || 0} />
        </div>
      </div>

      {/* Log filtrato per campagna */}
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="font-display text-lg font-black">Destinatari</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1" data-testid="log-filter">
              {[
                ["all", `Tutti (${logs.length})`],
                ["opened", `Aperti (${openedCount})`],
                ["clicked", `Click (${clickedCount})`],
                ["unsub", `Disiscritti (${unsubCount})`],
                ["error", `Errori (${errorCount})`],
              ].map(([k, l]) => (
                <button key={k} data-testid={`log-filter-${k}`} onClick={() => setLogFilter(k)}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${logFilter === k ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
                  {l}
                </button>
              ))}
            </div>
            <input
              data-testid="log-search"
              placeholder="Cerca email…"
              value={logSearch} onChange={(e) => setLogSearch(e.target.value)}
              className="sp-input !py-1.5 text-xs w-48"
            />
          </div>
        </div>

        {filteredLogs.length === 0 ? (
          <div className="sp-card p-8 text-center text-sm text-[color:var(--sp-text-muted)]">
            Nessun destinatario nella selezione corrente.
          </div>
        ) : (
          <div className="sp-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[color:var(--sp-subtle)]/40">
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">
                  <th className="px-4 py-3">Destinatario</th>
                  <th className="px-4 py-3">Inviata</th>
                  <th className="px-4 py-3">Aperture</th>
                  <th className="px-4 py-3">Click</th>
                  <th className="px-4 py-3">Stato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--sp-border)]">
                {filteredLogs.map((l) => (
                  <tr key={l.id} data-testid={`log-${l.id}`} className="hover:bg-[color:var(--sp-subtle)]/40">
                    <td className="px-4 py-3 text-xs">{l.to}</td>
                    <td className="px-4 py-3 text-[11px] text-[color:var(--sp-text-muted)] whitespace-nowrap">{new Date(l.sent_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-4 py-3 text-xs">{l.opens_count > 0 ? <span className="text-emerald-700 font-bold">{l.opens_count}×</span> : <span className="text-[color:var(--sp-text-muted)]">—</span>}</td>
                    <td className="px-4 py-3 text-xs">{l.clicks_count > 0 ? <span className="text-[color:var(--sp-brand)] font-bold">{l.clicks_count}×</span> : <span className="text-[color:var(--sp-text-muted)]">—</span>}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${l.unsubscribed_at ? "bg-rose-100 text-rose-700" : l.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                        {l.unsubscribed_at ? "Disiscritto" : l.status === "sent" ? "Inviata" : "Errore"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, tone, tid }) {
  const tones = {
    cyan: "text-cyan-700 bg-cyan-50 border-cyan-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    brand: "text-[color:var(--sp-brand)] bg-[color:var(--sp-brand)]/10 border-[color:var(--sp-brand)]/20",
    rose: "text-rose-700 bg-rose-50 border-rose-200",
  };
  return (
    <div className={`rounded-2xl border p-5 ${tones[tone] || tones.cyan}`} data-testid={tid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</div>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="text-4xl font-display font-black mt-2 tabular-nums" data-testid={`${tid}-value`}>{value}</div>
      <div className="text-[11px] mt-1 opacity-70">{sub}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-[color:var(--sp-text-muted)]">{label}</div>
      <div className="text-2xl font-display font-black mt-1 tabular-nums">{value}</div>
    </div>
  );
}
